import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import Spinner from '../../shared/spinner';
import InputMask from 'react-input-mask';
import { format, parse, startOfMonth, endOfMonth } from 'date-fns';
import { Empregador } from '../../shared/model/empregador';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { RelatoriosDTO } from '../../shared/dto/relatoriosDTO';
import EmpregadorService from '../../services/empregadorService';
import CustomAlert from '../../shared/customAlert';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import RelatorioService from '../../services/relatorioService';

function RelatorioExpectativaComponent() {
  useVerificaPermissao();
  
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  // Filtro de Competência (Mês/Ano)
  const [competencia, setCompetencia] = useState(format(new Date(), 'MM/yyyy'));

  // Select Simples de Convênio
  const [availableEmpregadores, setAvailableEmpregadores] = useState<Empregador[]>([]);
  const [selectedEmpregadorId, setSelectedEmpregadorId] = useState<number | ''>('');

  const relatorioService: RelatorioService = new RelatorioService();
  const empregadorService: EmpregadorService = new EmpregadorService();

  useEffect(() => {
    listarEmpregadores();
  }, []);

  const listarEmpregadores = async () => {
    setLoading(true);
    try {
      const response = await empregadorService.listarTodosEmpregadoresAtivos();
      setAvailableEmpregadores(response.data);
    } catch (err) {
      console.error(err);
      setAlert({ message: 'Erro ao carregar a lista de convênios.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorio = async () => {
    if (!selectedEmpregadorId) {
      return setAlert({ message: 'Selecione um Convênio', type: 'warning' });
    }
    if (!competencia || competencia.includes('_')) {
      return setAlert({ message: 'Preencha a competência corretamente (MM/AAAA)!', type: 'warning' });
    }

    setLoading(true);

    try {
      const dto: RelatoriosDTO = new RelatoriosDTO();
      
      const empSelecionado = availableEmpregadores.find(e => e.id === Number(selectedEmpregadorId));
      dto.empregadores = empSelecionado ? [empSelecionado] : [];
      
      // Converte "04/2026" para o dia 01 (dtBase1) e dia 30/31 (dtBase2)
      const dataReferencia = parse(competencia, "MM/yyyy", new Date());
      dto.dtBase1 = startOfMonth(dataReferencia);
      dto.dtBase2 = endOfMonth(dataReferencia);

      const response = await relatorioService.gerarRelatorioExpectativaCsv(dto);
      const contentType = response.headers['content-type'];

      if (contentType && contentType.includes('text/plain')) {
        const errorText = typeof response.data.text === 'function' ? await response.data.text() : response.data;
        setAlert({ message: errorText, type: 'warning' });
        setLoading(false);
        return;
      }

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `relatorio_expectativa_${competencia.replace('/', '-')}.csv`;
      
      if (contentDisposition && contentDisposition.includes('filename=')) {
        fileName = contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAlert({ message: 'Relatório gerado com sucesso!', type: 'success' });

    } catch (err: any) {
      console.error("Erro na geração do relatório:", err);
      setAlert({ message: 'Ocorreu um erro inesperado ao gerar o relatório.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}

      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header">
              <strong>Relatório de Expectativa (Conciliação)</strong>
            </div>
            <div className="card-body">
              
              {/* Filtro de Competência e Convênio na mesma linha */}
              <div className="row" style={{ marginTop: '10px' }}>
                
                <div className="col-md-3">
                  <label className="mb-2">Competência <span style={{ color: 'red' }}>*</span></label>
                  <InputMask 
                    required 
                    mask="99/9999" 
                    placeholder="MM/AAAA"
                    className="form-control" 
                    type="text" 
                    value={competencia} 
                    onChange={(e) => setCompetencia(e.target.value)} 
                  />
                </div>

                <div className="col-md-5">
                  <label className="mb-2">Convênio <span style={{ color: 'red' }}>*</span></label>
                  <select
                    className="form-select"
                    value={selectedEmpregadorId}
                    onChange={(e) => setSelectedEmpregadorId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Selecione um convênio...</option>
                    {availableEmpregadores.map((empregador) => (
                      <option key={empregador.id!} value={empregador.id ?? ''}>
                        {empregador.id} - {empregador.nome}
                      </option>
                    ))}
                  </select>
                </div>

              </div>

              {/* Botão Gerar */}
              <div className="row" style={{ marginTop: '30px', height: '50px' }}>
                <div className="col-md-3">
                  <button
                    className="btn"
                    id="App-button-cor-principal"
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', backgroundColor: '#343a40', color: 'white',
                      borderRadius: '5px', height: '100%'
                    }}
                    onClick={gerarRelatorio}
                  >
                    <FontAwesomeIcon icon={faPrint} style={{ fontSize: 'larger' }} />
                    <strong style={{ marginLeft: '10px', fontSize: 'larger' }}> Gerar Relatório CSV</strong>
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RelatorioExpectativaComponent;